import { useMemo } from 'react'
import { ImmutableVector3 } from '@modugen/ui-toolkit'
import { useModelStore } from '@editorStores'
import { useHiddenElements } from '@editorHooks'
import { VerticalOpeningHelper } from '../components/AlignmentHelper'

export const useVerticalOpeningHelpers = (): VerticalOpeningHelper[] => {
  const walls = useModelStore(state => state.model.walls)
  const showWallOpenings = useModelStore(state => state.showWallOpenings)
  const showVerticalOpeningAlignmentHelpers = useModelStore(
    state => state.showVerticalOpeningAlignmentHelpers,
  )
  const hiddenElements = useHiddenElements()

  return useMemo<VerticalOpeningHelper[]>(() => {
    if (!showWallOpenings || !showVerticalOpeningAlignmentHelpers) return []

    const openingPointsByXY: Record<
      string,
      {
        point: Point
        topZ: number
      }[]
    > = {}

    walls.forEach(wall => {
      if (hiddenElements.has(wall.guid)) return
      ;(wall.openings || []).forEach(opening => {
        if (opening.is_local) return

        const [point0, point1, point2] = opening.shape.points
        if (!point0 || !point1 || !point2) return

        const point0Key = `${point0.x}|${point0.y}`
        const point1Key = `${point1.x}|${point1.y}`
        const upperZ = point2.z

        openingPointsByXY[point0Key] = [
          ...(openingPointsByXY[point0Key] || []),
          { point: point0, topZ: upperZ },
        ]
        openingPointsByXY[point1Key] = [
          ...(openingPointsByXY[point1Key] || []),
          { point: point1, topZ: upperZ },
        ]
      })
    })

    return Object.values(openingPointsByXY).flatMap(points => {
      if (points.length < 2) return []

      const sortedByZ = [...points].sort((pointA, pointB) => pointA.point.z - pointB.point.z)

      const helpers: VerticalOpeningHelper[] = []
      for (let index = 0; index < sortedByZ.length - 1; index += 1) {
        const lowerOpeningPoint = sortedByZ[index].point
        const higherOpeningPoint = sortedByZ[index + 1].point
        helpers.push({
          lowerPoint: lowerOpeningPoint,
          higherPoint: new ImmutableVector3(
            higherOpeningPoint.x,
            higherOpeningPoint.y,
            sortedByZ[index + 1].topZ,
          ),
        })
      }

      return helpers
    })
  }, [hiddenElements, walls, showVerticalOpeningAlignmentHelpers, showWallOpenings])
}
